<template>
    <Loading v-if="!didLoadData" />
    <v-container v-else-if="!administrator && !isNew">
        <v-row>
            <v-col>
                <BackButton :target="{ name: 'displaysAdministrators' }">Seznam uživatelů</BackButton>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <PlaceHolder
                    path="/icons/something-went-wrong.png"
                    text="Je nám líto, něco se pokazilo. Zkuste znovu načíst stránku."
                    title="Něco se polámalo"
                />
            </v-col>
        </v-row>
    </v-container>
    <v-container v-else>
        <v-row class="font-weight-medium mb-3">
            <v-col cols="12" md="8" class="pa-0">
                <BackButton :target="{ name: 'displaysAdministrators' }">Seznam uživatelů</BackButton>
            </v-col>
            <v-col cols="12" md="4" align-self="center" class="d-flex justify-md-end justify-start pa-0">
                <SaveButton
                    v-if="!isSaved"
                    :disabled="isFormInvalid || updatedUser"
                    @save="saveUser"
                    label="Uložit"
                    data-cy="save"
                />
                <v-btn v-else outlined>
                    <v-icon left>check</v-icon>
                    Uloženo
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="pa-0">
                <h1 v-if="!isNew" class="font-weight-medium">{{ administrator.fullName }}</h1>
                <div v-else>
                    <h1 class="font-weight-medium">Nový uživatel</h1>
                    <span class="subtitle">Zadejte login a jméno uživatele a přiřaďte mu potřebná práva</span>
                </div>
            </v-col>
        </v-row>
        <v-row v-if="!isNew" class="mb-1">
            <v-col class="pa-0">
                <span class="subtitle">{{ administrator.xlogin }}</span>
            </v-col>
        </v-row>
        <v-row v-else-if="!controlData" class="mr-1">
            <v-col cols="6" class="pl-0">
                <v-text-field
                    color="light-green darken-1"
                    v-model="xlogin"
                    label="xlogin uživatele"
                    :rules="[requiredRule]"
                    data-cy="xlogin"
                />
            </v-col>
            <v-col cols="6">
                <v-text-field
                    color="light-green darken-1"
                    v-model="fullName"
                    label="Jméno a příjmení uživatele"
                    :rules="[requiredRule]"
                    data-cy="fullName"
                />
            </v-col>
        </v-row>
        <v-row v-if="!controlData" class="mt-7">
            <v-col class="pa-0">
                <v-container fluid>
                    <v-row class="mb-1">
                        <v-col class="pa-0">
                            <h3 class="font-weight-medium">Práva k fakultám a součástem</h3>
                            <div class="subtitle">Vyberte práva, která chcete uživateli přiřadit</div>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pa-0">
                            <FacultiesCards
                                :input="getFaculties"
                                data="faculty"
                                @clicked="onClickChild"
                                :selectedItems="selectedFaculties"
                                data-cy="card-faculty"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="!isNew">
                        <v-col class="pa-0">
                            <span class="removeButton mt-3" @click="attemptToDeleteAdministrator()">
                                ODSTRANIT UŽIVATELE
                            </span>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col>
                <PlaceHolder 
                    path="/icons/something-went-wrong.png"
                    text="Je nám líto, něco se pokazilo. Zkuste znovu načíst stránku."
                    title="Něco se polámalo"
                />
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import BackButton from "@/components/layout/BackButton";
import Loading from "@/components/Loading";
import FacultiesCards from "@/components/displays/FacultiesCards";
import SaveButton from "@/components/buttons/SaveButton";
import PlaceHolder from "@/components/news/PlaceHolder";

const store = useStore();
const router = useRouter();
const route = useRoute();

const facultiesIds = ref([]);
const updatedUser = ref(true);
const isSaved = ref(false);
const xlogin = ref("");
const fullName = ref("");
const requiredRule = (value) => !!value || 'Povinné';

const administratorId = computed(() => parseInt(route.params.administratorId));
const isNew = computed(() => route.params.administratorId === "new");
const administrator = computed(() => store.getters['displaysAdmins/getAdministratorById'](administratorId.value));
const didLoadData = computed(() => store.getters['displaysAdmins/didLoadAdministrators'] && store.getters['displaysFaculties/didLoadFaculties']);
const controlData = computed(() => store.getters['displaysFaculties/getFaculties']?.length === 0);
const getFaculties = computed(() => store.getters['displaysFaculties/getFaculties']);
const selectedFaculties = computed(() => !isNew.value 
    ? store.getters['displaysFaculties/getFaculties'].filter(faculty => administrator.value.faculties.some(item => item.id === faculty.value.id)) 
    : []);

const isFormInvalid = computed(() => {
    const invalidCards = facultiesIds.value.length === 0;
    return isNew.value ? (invalidCards || !xlogin.value || !fullName.value) : invalidCards;
});

const fetchInitialData = async () => {
    await Promise.all([
        store.dispatch('displaysFaculties/fetchAllFaculties'),
        store.dispatch('displaysAdmins/fetchAdministrators')
    ]);
};

const attemptToDeleteAdministrator = async () => {
    if (window.confirm(`Opravdu chcete smazat užívatele ${administrator.value.fullName} ?`)) {
        await Promise.all([
            store.dispatch('displaysAdmins/deleteAdministrator', administratorId.value),
            router.push({ name: 'displaysAdministrators' })
        ]);
    }
};

const onClickChild = (obj) => {
    facultiesIds.value = obj.value;
    updatedUser.value = false;
};

const saveUser = async () => {
    if (!isNew.value) {
        await store.dispatch('displaysAdmins/updateAdministrator', {
            id: administratorId.value,
            request: {
                facultyIds: facultiesIds.value,
                fullName: administrator.value.fullName,
                xlogin: administrator.value.xlogin,
            }
        });
    } else {
        await store.dispatch('displaysAdmins/createAdministrator', {
            facultyIds: facultiesIds.value,
            fullName: fullName.value,
            xlogin: xlogin.value,
        });
    }
    isSaved.value = true;
    await router.push({ name: 'displaysAdministrators' });
    window.scrollTo(0, 0);
};

onMounted(fetchInitialData);
</script>

<style scoped>
.subtitle {
    font-size: 16px;
    color: #6D7278;
}

.removeButton {
    color: #E02020;
    font-size: 18px;
    font-weight: 500;
    cursor: pointer;
}
</style>
